import SchemaProvider from 'titus-ts/dist/js/MetadataRenderer/Schema/SchemaProvider';
import {
    ClassificationEventName,
    ClassificationEventType,
    EventPayload,
    Metadata,
    SeverityLevel,
    OfficeDocument,
    DocumentContext,
    DocumentEvent,
} from './auditEventTypes';
import AppService from '../../services/appService';
import { IItemAuditDataProvider } from './auditDataProvider';
import { OfficeApi } from '../../office/OfficeApi';
import DocumentTypeEnum from 'titus-ts/dist/js/Office/DocumentTypeEnum';
import { localSettingsService } from '../localSettingsService';

// interfaces to accomodate seperate providers for specific document types
export interface IDocumentDataProvider {
    getDocumentAuditProperties(): Promise<OfficeDocument>;
}
export interface IDocumentContextDataProvider {
    getDocumentContextAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<DocumentContext>;
}

export class DocumentAuditDataProvider
    implements IDocumentDataProvider, IDocumentContextDataProvider, IItemAuditDataProvider
{
    private officeApi: OfficeApi;
    private schemaProvider: SchemaProvider;
    private appService: AppService;

    constructor(officeApi: OfficeApi, schemaProvider: SchemaProvider, appService: AppService) {
        this.officeApi = officeApi;
        this.schemaProvider = schemaProvider;
        this.appService = appService;
    }

    public async getItemAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<EventPayload> {
        const [document, documentContext] = await Promise.all([
            this.getDocumentAuditProperties(),
            this.getDocumentContextAuditProperties(event, eventType),
        ]);

        return {
            DocumentContext: documentContext,
            Document: document,
        } as DocumentEvent;
    }

    public async getDocumentAuditProperties(): Promise<OfficeDocument> {
        const [filename, path, documentType, metadata, originalMetadata] = await Promise.all([
            this.officeApi.getFileName(),
            this.officeApi.getFilePath(),
            this.getDocumentType(),
            this.getMetadata(),
            this.getOriginalMetadata(),
        ]);

        const documentAuditProperties = {
            Filename: filename,
            Path: path,
            DocumentType: documentType,
            Metadata: metadata,
            OriginalMetadata: originalMetadata,
            OriginalField: {},
            NewField: {},
        } as OfficeDocument;

        return documentAuditProperties;
    }

    public async getDocumentContextAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<DocumentContext> {
        const documentContextAuditProperties = {
            UserName: this.getUserName(),
            Computer: this.getComputerName(),
            Event: event,
            EventType: eventType,
            Product: this.getProductName(),
            Version: this.getProductVersion(),
            Platform: this.getPlatform(),
            Severity: this.getSeverity(event, eventType),
        } as DocumentContext;

        return documentContextAuditProperties;
    }

    private getDocumentType(): string {
        const docType = this.officeApi.getDocumentType();

        return DocumentTypeEnum[docType];
    }

    private getMetadata(): Metadata {
        const metadata = this.schemaProvider.getMetadata();

        const auditMetadata = Object.entries(metadata).map(([key, value]) => ({
            FieldName: key,
            Values: (Array.isArray(value) ? value : [value]).map((v) => ({
                Value: v,
            })),
        }));

        return auditMetadata;
    }

    private getOriginalMetadata(): Metadata {
        const originalMetadata = this.schemaProvider.getOriginalMetadata();

        const auditOriginalMetadata = Object.entries(originalMetadata).map(([key, value]) => ({
            FieldName: key,
            Values: (Array.isArray(value) ? value : [value]).map((v) => ({
                Value: v,
            })),
        }));

        return auditOriginalMetadata;
    }

    private getUserName(): string | undefined {
        return undefined;
    }

    private getComputerName(): string | undefined {
        return undefined;
    }

    private getProductName(): string {
        return this.appService.getProductName();
    }

    private getProductVersion(): string {
        return localSettingsService.getLocalSettings().dcsOneAboutInfo.version;
    }

    private getPlatform(): string {
        return this.officeApi.getPlatformType() || '';
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getSeverity(event: ClassificationEventName, eventType: ClassificationEventType): SeverityLevel {
        return 'Informational' as SeverityLevel;
    }
}
